import type { ReactElement } from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { AuditLog } from '@kirin/api-client/contracts/audit-logs-table';
import { OperationBadge } from './OperationBadge';
import { formatAuditUserDisplay } from './utils';

/** Raw `table_name`, shown as-is so it matches the dropdown option labels. */
export function ObjectTypeCell({
  tableName,
}: {
  tableName: string;
}): ReactElement {
  return <span className="text-sm">{tableName}</span>;
}

/**
 * Originating surface for policy edits, read from `metadata.source` recorded by
 * `audit_trigger_func` via the `app.audit_metadata` GUC (see migration 0110).
 * Renders "Alerts" / "Policies" as a small badge; dash when no source is
 * recorded (login/logout, non-policy tables, or pre-KIR-3550 rows).
 */
export function AuditSourceCell({
  metadata,
}: {
  metadata: Record<string, unknown> | null;
}): ReactElement {
  const rawSource = metadata?.source;
  const source = typeof rawSource === 'string' ? rawSource : null;
  if (source !== 'alerts' && source !== 'policies') {
    return <span className="text-muted-foreground text-sm">-</span>;
  }
  const label = source === 'alerts' ? 'Alerts' : 'Policies';
  return (
    <span className="inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium text-muted-foreground">
      {label}
    </span>
  );
}

/** Colored outline badge for the operation. */
export function OperationBadgeCell({
  operation,
}: {
  operation: AuditLog['operation'];
}): ReactElement {
  return <OperationBadge operation={operation} />;
}

/** Full monospace record id, wrapping within the column; `-` when null. */
export function ObjectIdCell({
  recordId,
}: {
  recordId: string | null;
}): ReactElement {
  if (!recordId) {
    return <span className="text-muted-foreground">-</span>;
  }

  return (
    <span className="font-mono break-all text-muted-foreground">
      {recordId}
    </span>
  );
}

/** User display as name → email → "System"; the raw id is never shown. */
export function AuditLogUserCell({
  userName,
  userEmail,
}: {
  userName: string | null;
  userEmail: string | null;
}): ReactElement {
  const display = formatAuditUserDisplay(userName, userEmail);

  if (userName && userEmail) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help text-sm">{display}</span>
          </TooltipTrigger>
          <TooltipContent>{userEmail}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return <span className="text-sm">{display}</span>;
}

/** Collapsible from→to diff of changed fields; "No changes" when empty. */
export function ChangedFieldsDisplay({
  changedFields,
}: {
  changedFields: Record<string, unknown> | null;
}): ReactElement {
  const [isOpen, setIsOpen] = useState(false);

  if (!changedFields || Object.keys(changedFields).length === 0) {
    return <span className="text-muted-foreground text-sm">No changes</span>;
  }

  const fieldCount = Object.keys(changedFields).length;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="flex items-center gap-1 h-auto p-1 max-w-full overflow-hidden"
        >
          {isOpen ? (
            <ChevronDown className="h-4 w-4 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 flex-shrink-0" />
          )}
          <span className="text-sm truncate">
            {fieldCount} field{fieldCount !== 1 ? 's' : ''} changed
          </span>
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <div className="max-h-64 overflow-y-auto overflow-x-auto rounded border bg-muted/30 p-2">
          <div className="space-y-2 text-sm">
            {Object.entries(changedFields).map(([field, change]) => {
              const changeValue = change as { from: unknown; to: unknown };
              return (
                <div
                  key={field}
                  className="border-l-2 border-muted-foreground/30 pl-3"
                >
                  <div className="font-medium break-words">{field}</div>
                  <div className="text-muted-foreground break-words">
                    <span className="line-through">
                      {changeValue.from !== null &&
                      changeValue.from !== undefined
                        ? JSON.stringify(changeValue.from)
                        : 'null'}
                    </span>
                    {' → '}
                    <span className="font-medium">
                      {changeValue.to !== null && changeValue.to !== undefined
                        ? JSON.stringify(changeValue.to)
                        : 'null'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
